import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { LogIn } from "lucide-react";
import { useEffect } from "react";
import { verifyEmailLogin } from "@/auth/api";
import { meQuery } from "@/auth/queries";
import { safeRedirect } from "@/auth/redirect";
import { AuthLayout, Notice } from "@/components/AuthLayout";
import { ContentError } from "@/content/loader";
import { fr } from "@/i18n/fr";

/**
 * The page a magic link opens (`/connexion/verifier?token=`). The token is posted on a click, not
 * on load: mail scanners open the links they find, and a token works only once.
 */
export default function VerifyLogin() {
	const { token } = useSearch({ from: "/connexion/verifier" });
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const verify = useMutation({
		mutationFn: verifyEmailLogin,
		onSuccess: async ({ user, next }) => {
			queryClient.setQueryData(meQuery().queryKey, user);
			// replace: the used token leaves the history
			await navigate({ href: safeRedirect(next), replace: true });
		},
	});
	useEffect(() => {
		document.title = `${fr.signIn} · ${fr.appTitle}`;
	}, []);
	const expired =
		verify.error instanceof ContentError && verify.error.status === 400;
	const problem = !token
		? fr.verify.missing
		: expired
			? fr.verify.invalid
			: verify.isError
				? fr.login.failed
				: null;

	return (
		<AuthLayout title={fr.verify.title} lead={fr.verify.lead}>
			{problem && <Notice tone="danger">{problem}</Notice>}
			{token && !expired ? (
				<button
					type="button"
					className="ui-btn-primary w-full"
					disabled={verify.isPending || verify.isSuccess}
					onClick={() => verify.mutate(token)}
				>
					<LogIn className="size-5" aria-hidden="true" />
					{verify.isPending || verify.isSuccess
						? fr.verify.pending
						: fr.verify.submit}
				</button>
			) : (
				<Link to="/connexion" className="ui-btn w-full no-underline">
					{fr.verify.again}
				</Link>
			)}
		</AuthLayout>
	);
}
